import { Platform, StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { getTheme } from '../../../src/theme/getTheme';
import { makeBoxShadow, makeTextShadow } from '../../../src/shared/helpers/shadowStyles.helper';

export type SuccessModalStyleOptions = {
  insets?: { top: number; bottom: number; left: number; right: number };
  isNarrowScreen?: boolean;
};

const DEFAULT_INSETS = { top: 0, bottom: 0, left: 0, right: 0 };
const glow = (color: string, opacity: number, radius: number) =>
  makeBoxShadow({ shadowColor: color, shadowOffset: { width: 0, height: 0 }, shadowOpacity: opacity, shadowRadius: radius });
const textGlow = (color: string, radius: number) =>
  makeTextShadow({ textShadowColor: color, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: radius });

export const getSuccessModalStyles = (theme: Theme, options?: SuccessModalStyleOptions) => {
  const insets = options?.insets ?? DEFAULT_INSETS;
  const safeTop = Math.max(insets.top, Platform.OS === 'web' ? 16 : 8);
  const safeBottom = Math.max(insets.bottom, Platform.OS === 'web' ? 24 : 16);
  const isNarrowScreen = options?.isNarrowScreen ?? false;

  return StyleSheet.create({
    successModalContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#09071B',
      ...(Platform.OS === 'web' && { position: 'fixed' as never, top: 0, right: 0, bottom: 0, left: 0, zIndex: 9999 }),
    },
    backgroundImage: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
    backgroundScrim: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: isNarrowScreen ? 'rgba(8, 5, 22, 0.08)' : 'rgba(8, 5, 22, 0.04)',
    },
    fxLayer: { ...StyleSheet.absoluteFillObject, overflow: 'hidden' },
    centerBlast: {
      position: 'absolute',
      top: '48%',
      left: '50%',
      width: Platform.OS === 'web' ? 360 : 260,
      height: Platform.OS === 'web' ? 360 : 260,
      marginTop: Platform.OS === 'web' ? -180 : -130,
      marginLeft: Platform.OS === 'web' ? -180 : -130,
      borderRadius: Platform.OS === 'web' ? 180 : 130,
      borderWidth: 1,
      borderColor: 'rgba(246, 224, 94, 0.18)',
      backgroundColor: 'rgba(107, 70, 193, 0.2)',
      opacity: 0.34,
      ...(Platform.OS === 'web' ? { boxShadow: `0px 0px 70px 34px ${theme.primary}33` as const } : glow(theme.primary, 0.2, 70)),
    },
    sparkLayer: { ...StyleSheet.absoluteFillObject },
    sparkBase: { position: 'absolute', top: 0, left: 0, borderRadius: 999 },
    sparkSizeEmber: { width: 5, height: 5 },
    sparkSizeFleck: { width: 4, height: 10 },
    sparkColorGold: { backgroundColor: theme.accent, ...(Platform.OS === 'web' ? { boxShadow: `0px 0px 13px ${theme.accent}` as const } : undefined) },
    sparkColorAmber: { backgroundColor: theme.accentDark, ...(Platform.OS === 'web' ? { boxShadow: `0px 0px 12px ${theme.accentDark}` as const } : undefined) },
    sparkColorPurple: { backgroundColor: theme.primaryLight, ...(Platform.OS === 'web' ? { boxShadow: `0px 0px 11px ${theme.primaryLight}` as const } : undefined) },
    sparkColorWhite: { backgroundColor: theme.white, ...(Platform.OS === 'web' ? { boxShadow: '0px 0px 10px rgba(255, 255, 255, 0.86)' as const } : undefined) },
    successModalContentTouchable: { flex: 1, width: '100%' },
    successModalContentSurface: {
      flex: 1,
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: safeTop,
      paddingBottom: safeBottom,
      paddingHorizontal: Platform.OS === 'web' ? 28 : 14,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { height: '100vh' as never }),
    },
    successModalContentSurfaceSmall: {
      justifyContent: 'space-between',
      paddingTop: Math.max(safeTop, 12),
      paddingBottom: Math.max(safeBottom, 18),
      paddingHorizontal: 12,
    },
    successModalContentWrapper: {
      width: '100%',
      maxWidth: '100%',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'space-between',
      alignSelf: 'center',
    },
    successBanner: {
      alignItems: 'center',
      marginTop: Platform.OS === 'web' ? 28 : 18,
      marginBottom: Platform.OS === 'web' ? 26 : 12,
      paddingHorizontal: 16,
      ...(Platform.OS === 'web' && { maxWidth: 620 }),
    },
    successBannerSmall: { marginTop: 8, marginBottom: 8 },
    eyebrowText: {
      marginBottom: Platform.OS === 'web' ? 8 : 5,
      color: theme.primaryLight,
      fontSize: Platform.OS === 'web' ? 15 : 12,
      fontWeight: '800',
      letterSpacing: 4,
      textTransform: 'uppercase',
      ...makeTextShadow({ textShadowColor: 'rgba(0, 0, 0, 0.9)', textShadowOffset: { width: 0, height: 2 }, textShadowRadius: 8 }),
    },
    eyebrowTextSmall: { marginBottom: 4, fontSize: 11, letterSpacing: 3 },
    successBannerTitle: {
      color: theme.white,
      fontSize: Platform.OS === 'web' ? 60 : 44,
      lineHeight: Platform.OS === 'web' ? 64 : 46,
      fontWeight: '900',
      fontStyle: 'italic',
      letterSpacing: Platform.OS === 'web' ? 3 : 1.5,
      textAlign: 'center',
      textTransform: 'uppercase',
      ...makeTextShadow({ textShadowColor: 'rgba(0, 0, 0, 0.85)', textShadowOffset: { width: 3, height: 5 }, textShadowRadius: 8 }),
    },
    successBannerTitleAccent: {
      marginBottom: Platform.OS === 'web' ? 12 : 8,
      color: theme.accent,
      ...makeTextShadow({ textShadowColor: 'rgba(85, 60, 154, 0.75)', textShadowOffset: { width: 2, height: 4 }, textShadowRadius: 10 }),
    },
    successBannerTitleSmall: { fontSize: 36, lineHeight: 38 },
    successBannerSubtitle: {
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: Platform.OS === 'web' ? 16 : 13,
      lineHeight: Platform.OS === 'web' ? 22 : 18,
      fontWeight: '700',
      letterSpacing: 1.2,
      textAlign: 'center',
      textTransform: 'uppercase',
      ...makeTextShadow({ textShadowColor: 'rgba(0, 0, 0, 0.8)', textShadowOffset: { width: 0, height: 2 }, textShadowRadius: 6 }),
    },
    successBannerSubtitleSmall: { fontSize: 11, lineHeight: 16 },
    successBannerName: {
      marginTop: 3,
      color: theme.accentLight,
      fontSize: Platform.OS === 'web' ? 22 : 17,
      fontWeight: '900',
      textAlign: 'center',
      textTransform: 'uppercase',
      ...textGlow('rgba(246, 224, 94, 0.45)', 8),
    },
    successBannerNameSmall: { fontSize: 15 },
    charactersContainer: {
      width: '100%',
      maxWidth: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      flexWrap: 'nowrap',
      marginVertical: Platform.OS === 'web' ? 10 : 4,
      paddingHorizontal: Platform.OS === 'web' ? 34 : 8,
      ...(Platform.OS !== 'web' && { flexShrink: 1, gap: 4 }),
    },
    charactersContainerSmall: { justifyContent: 'space-between', marginVertical: 4, paddingHorizontal: 0, gap: 0 },
    charactersContainerNarrow: { maxWidth: '100%', paddingHorizontal: 0 },
    characterWrapper: {
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      minWidth: Platform.OS === 'web' ? 220 : 120,
      ...(Platform.OS === 'web' ? { flex: 0, marginHorizontal: 10 } : { flex: 1, maxWidth: '42%' }),
    },
    characterWrapperSmall: { minWidth: 96, maxWidth: 128 },
    successModalAvatar: { width: Platform.OS === 'web' ? 210 : 168, height: Platform.OS === 'web' ? 210 : 168 },
    successModalAvatarSmall: { width: 118, height: 118 },
    successModalAvatarRight: { marginLeft: 0 },
    characterLabel: {
      marginTop: Platform.OS === 'web' ? 12 : 7,
      color: theme.primaryLight,
      fontSize: Platform.OS === 'web' ? 12 : 10,
      fontWeight: '900',
      letterSpacing: 2,
      textTransform: 'uppercase',
    },
    characterLabelAway: { color: theme.accentLight },
    characterLabelSmall: { marginTop: 6, fontSize: 9 },
    characterName: {
      alignSelf: 'stretch',
      marginTop: 3,
      color: theme.white,
      fontSize: Platform.OS === 'web' ? 22 : 15,
      fontWeight: '900',
      textAlign: 'center',
      ...makeTextShadow({ textShadowColor: 'rgba(0, 0, 0, 0.85)', textShadowOffset: { width: 1, height: 3 }, textShadowRadius: 6 }),
    },
    characterNameSmall: { alignSelf: 'stretch', fontSize: 13 },
    characterNameFriend: { marginLeft: 0 },
    vsBadge: {
      zIndex: 10,
      width: Platform.OS === 'web' ? 116 : 82,
      height: Platform.OS === 'web' ? 116 : 82,
      alignItems: 'center',
      justifyContent: 'center',
      marginHorizontal: Platform.OS === 'web' ? 14 : -6,
      borderRadius: Platform.OS === 'web' ? 58 : 41,
      borderWidth: 2,
      borderColor: theme.accent,
      backgroundColor: 'rgba(10, 5, 28, 0.72)',
      transform: [{ rotate: '-8deg' }],
      ...(Platform.OS === 'web' ? { boxShadow: `0px 0px 24px ${theme.accent}66` as const } : glow(theme.accent, 0.34, 20)),
    },
    vsBadgeSmall: { width: 62, height: 62, marginHorizontal: -10, borderRadius: 31 },
    vsText: {
      color: theme.accent,
      fontSize: Platform.OS === 'web' ? 44 : 32,
      fontWeight: '900',
      fontStyle: 'italic',
      letterSpacing: -2,
      ...makeTextShadow({ textShadowColor: 'rgba(85, 60, 154, 0.9)', textShadowOffset: { width: 1, height: 3 }, textShadowRadius: 7 }),
    },
    vsTextSmall: { fontSize: 24 },
    countdownContainer: { width: '100%', alignItems: 'center', marginTop: Platform.OS === 'web' ? 24 : 10, marginBottom: Platform.OS === 'web' ? 18 : 4 },
    countdownContainerSmall: { marginTop: 8, padding: 0 },
    countdownLabel: {
      marginBottom: Platform.OS === 'web' ? 10 : 5,
      color: 'rgba(255, 255, 255, 0.88)',
      fontSize: Platform.OS === 'web' ? 15 : 11,
      fontWeight: '900',
      letterSpacing: Platform.OS === 'web' ? 2.4 : 1.2,
      textTransform: 'uppercase',
      ...makeTextShadow({ textShadowColor: 'rgba(0, 0, 0, 0.8)', textShadowOffset: { width: 0, height: 2 }, textShadowRadius: 5 }),
    },
    countdownLabelSmall: { marginBottom: 5, fontSize: 10 },
    countdownTimerBox: {
      marginBottom: Platform.OS === 'web' ? 10 : 6,
      paddingHorizontal: Platform.OS === 'web' ? 34 : 18,
      paddingVertical: Platform.OS === 'web' ? 15 : 10,
      borderRadius: Platform.OS === 'web' ? 36 : 26,
      borderWidth: Platform.OS === 'web' ? 2 : 1.5,
      borderColor: theme.accentDark,
      backgroundColor: 'rgba(3, 7, 24, 0.72)',
      ...(Platform.OS === 'web' ? { boxShadow: `0px 0px 20px ${theme.primary}55` as const } : glow(theme.primary, 0.24, 18)),
    },
    countdownTimerBoxSmall: { marginBottom: 5, paddingHorizontal: 18, paddingVertical: 8, borderRadius: 24 },
    countdownTimer: {
      color: theme.accent,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
      fontSize: Platform.OS === 'web' ? 46 : 28,
      fontWeight: '300',
      letterSpacing: Platform.OS === 'web' ? 5 : 2,
      ...textGlow('rgba(246, 224, 94, 0.62)', 8),
    },
    countdownTimerSmall: { fontSize: 25 },
    countdownHint: {
      marginBottom: Platform.OS === 'web' ? 8 : 5,
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: Platform.OS === 'web' ? 14 : 11,
      fontWeight: '700',
      textAlign: 'center',
    },
    countdownHintSmall: { marginBottom: 5, fontSize: 10 },
  });
};

export const successModalStyles = getSuccessModalStyles(getTheme('light'));
